import { Button } from '@material-ui/core'
import { Box, Grid, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'
import { RootStateOrAny } from 'react-redux'
import { useInput } from '../../../hooks/useInput'
import {
  PROMO_CODE_SEND_RESET,
  sendPromoCodeEmail,
} from '../../../modules/promoCodeSend'
import { Context } from '../../../providers/ProjectContextProvider'
import { useAppDispatch, useAppSelector } from '../../../store'
import FormContainer from '../../Form/FormContainer'
import SelectInput from '../../Form/SelectInput'
import Message from '../../Message/Message'
import ModalPopUp from '../../ModalPopUp/ModalPopUp'
import ToastMessage from '../../ToastMessage/ToastMessage'

const PromoCodeModal = () => {
  const dispatch = useAppDispatch()

  const { selectedProjects, showPromoCodeModal, setShowPromoCodeModal } =
    useContext(Context)

  const { error, promoCodes } = useAppSelector(
    (state: RootStateOrAny) => state.promoCodes
  )

  const {
    error: promoCodeSendError,
    loading,
    success,
  } = useAppSelector((state) => state.promoCodeSend)

  const { value: promoCode, bind: bindPromoCode } = useInput('')

  const [showToast, setShowToast] = useState(false)

  const selectedCode = promoCodes.find((code: PromoCode) => {
    return code.name === promoCode
  })

  const formattedList = promoCodes
    .filter((code: PromoCode) => {
      if (!code.active) {
        return false // Filter out inactive codes
      }

      if (code.expires && code.start && code.end) {
        const today = new Date()
        const startDate = new Date(code.start)
        const endDate = new Date(code.end)
        return startDate <= today && today <= endDate // Check if today is within the start and end date
      }

      return true // Include codes that don't expire
    })
    .map((code: PromoCode) => ({
      label: code.name,
      value: code.name,
    }))

  const handleSubmit = () => {
    dispatch(sendPromoCodeEmail(selectedCode, selectedProjects))
  }

  useEffect(() => {
    if (success) {
      setShowToast(true)
      setShowPromoCodeModal(false)
      dispatch({ type: PROMO_CODE_SEND_RESET })
    }
  }, [success])

  return (
    <>
      <ModalPopUp
        title="Send promo codes"
        setShowModal={setShowPromoCodeModal}
        showModal={showPromoCodeModal}
        variant="medium"
      >
        <FormContainer>
          <h4 className="m-b-30">Send promo codes</h4>

          <Typography sx={{ mb: 4, mt: -2 }}>
            Please select the promo code you want to email to send to project
            owners and contributors.
          </Typography>

          <Grid container spacing={4} sx={{ minHeight: 186 }}>
            <Grid item xs={4} sx={{ mt: -1 }}>
              <SelectInput
                label="Promo code"
                name="promoCodes"
                options={formattedList}
                bindValue={bindPromoCode}
              />
            </Grid>

            <Grid item xs={4}>
              <Typography
                sx={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.2px' }}
              >
                Promo code details
              </Typography>
              {selectedCode && (
                <>
                  <Typography sx={{ mt: 2 }}>
                    Name: {selectedCode.name}
                  </Typography>
                  <Typography>Value: {selectedCode.value}%</Typography>
                  <Typography>
                    Expires: {selectedCode.expires ? 'Yes' : 'No'}
                  </Typography>

                  {selectedCode.expires && (
                    <>
                      <Typography>
                        Start date:{' '}
                        {selectedCode.expires &&
                          selectedCode.start &&
                          dayjs(selectedCode.start).format('MM/DD/YYYY')}
                      </Typography>
                      <Typography>
                        End date:{' '}
                        {selectedCode.expires &&
                          selectedCode.end &&
                          dayjs(selectedCode.end).format('MM/DD/YYYY')}
                      </Typography>
                    </>
                  )}
                </>
              )}
            </Grid>

            <Grid item xs={4}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.2px',
                  mb: 2,
                }}
              >
                Projects selected
              </Typography>

              <Box sx={{ maxHeight: 154, overflowY: 'scroll' }}>
                {selectedProjects.map((project, index) => (
                  <Typography key={`project-${index}`}>
                    {project.name}
                  </Typography>
                ))}
              </Box>
            </Grid>
          </Grid>

          {error && <Message variant="error">{error}</Message>}

          {promoCodeSendError && (
            <Message variant="error">
              Something went wrong. Please try again.
            </Message>
          )}

          <Box sx={{ mt: 8, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              color="primary"
              variant="outlined"
              className="m-r-24"
              onClick={() => setShowPromoCodeModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmit}
              disabled={!promoCode || loading}
            >
              Send Email
            </Button>
          </Box>
        </FormContainer>
      </ModalPopUp>

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Message sent.</p>
            <p className="fs14 m-b-0">
              Promo code has been successfully delivered.
            </p>
          </>
        }
        open={showToast}
        setOpen={setShowToast}
      />
    </>
  )
}

export default PromoCodeModal
